import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { clearParentalData, updateOrderData } from '@/store/slices/formSlice'
import { FormSection } from '@/types/FormSectionInterface'
import { OrderData, TestType } from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import React, { forwardRef, useImperativeHandle } from 'react'

interface OrderTypeProps {
  initialData?: Record<string, unknown>
  onDataChange?: (data: OrderData) => void
}

const OrderType = forwardRef<FormSection, OrderTypeProps>(({ initialData, onDataChange }, ref) => {
  const dispatch = useAppDispatch()
  const { formData } = useAppSelector((state) => state.form)

  // Use Redux state as the source of truth, fallback to props for initial data
  const selectedOption = formData.order.selectedOption || (initialData?.selectedOption as TestType) || TestType.NONE

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    const newData = { selectedOption: value as TestType }

    // Update Redux state
    dispatch(updateOrderData(newData))

    // Clear parental data when patient only is selected
    if (value === TestType.rWGS_Patient_Only) {
      dispatch(clearParentalData())
    }

    // Also call the callback for backward compatibility
    onDataChange?.(newData)
  }

  // Extract the UI content into a separate component to avoid duplication
  const OrderTypeContent = () => (
    <div className="space-y-6">
      <h4 className="section-header no-hover border-b border-gray-200 pb-2">
        Choose an order type. <span className="text-red-500">*</span>
      </h4>

      <div>
        <label className="form-label mb-3 block">Ultra Rapid Whole Genome Sequencing (urWGS)</label>
        <div className="space-y-2">
          <label className="flex cursor-pointer items-center">
            <input
              type="radio"
              name="selectedOption"
              value={TestType.urWGS}
              checked={selectedOption === TestType.urWGS}
              onChange={handleRadioChange}
              className="h-4 w-4 cursor-pointer border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="body-text ml-2">
              urWGS Trio (one individual and two biological parents){' '}
              <span className="text-xs text-gray-500"> (F11-03)</span>
            </span>
          </label>
        </div>
      </div>

      <div>
        <label className="form-label mb-3 block">Rapid Whole Genome Sequencing (rWGS)</label>
        <div className="space-y-2">
          <label className="flex cursor-pointer items-center">
            <input
              type="radio"
              name="selectedOption"
              value={TestType.rWGS_Patient_Only}
              checked={selectedOption === TestType.rWGS_Patient_Only}
              onChange={handleRadioChange}
              className="h-4 w-4 cursor-pointer border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="body-text ml-2">
              rWGS Patient Only (one individual) <span className="text-xs text-gray-500"> (F10-01)</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-center">
            <input
              type="radio"
              name="selectedOption"
              value={TestType.rWGS_Duo}
              checked={selectedOption === TestType.rWGS_Duo}
              onChange={handleRadioChange}
              className="h-4 w-4 cursor-pointer border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="body-text ml-2">
              rWGS Duo (one individual and one biological parent){' '}
              <span className="text-xs text-gray-500"> (F10-02)</span>
            </span>
          </label>
          <label className="flex cursor-pointer items-center">
            <input
              type="radio"
              name="selectedOption"
              value={TestType.rWGS_Trio}
              checked={selectedOption === TestType.rWGS_Trio}
              onChange={handleRadioChange}
              className="h-4 w-4 cursor-pointer border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="body-text ml-2">
              rWGS Trio (one individual and two biological parents){' '}
              <span className="text-xs text-gray-500"> (F10-03)</span>
            </span>
          </label>
        </div>
      </div>
    </div>
  )

  // Implement the FormSection interface
  useImperativeHandle(ref, () => ({
    isSectionComplete: () => {
      return FormCompletionChecker.isOrderComplete({ selectedOption })
    },
    renderSection: () => {
      // Return the main component render to avoid duplication
      return <OrderTypeContent />
    },
    renderSectionInfo: () => {
      return (
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <p className="body-text mb-3">
            Whole genome sequencing empowers clinicians to improve the time to treatment for critically ill patients,
            changing outcomes. Inflection Medicine offers two services:
          </p>
          <p className="form-label mb-2">Rapid Whole Genome Sequencing (rWGS)</p>
          <p className="body-text mb-3">
            Preliminary positive report within 5 business days, final report in 7 business days.
          </p>
          <p className="form-label mb-2">Ultra Rapid Whole Genome Sequencing (urWGS)</p>
          <p className="body-text mb-3">
            The turnaround time for ultra-rapid WGS is 3 calendar days when all necessary samples are received at the
            outset; if parental samples are delayed (up to 2 days post-patient), a final report will follow within 7
            days..
          </p>
        </div>
      )
    },
  }))

  return <OrderTypeContent />
})

OrderType.displayName = 'OrderType'

export default OrderType
